import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface SearchResult {
  id: string;
  noteSpaceId: string;
  noteSpaceName: string;
  content: string;
  type: string;
  position: { x: number; y: number };
  data: any;
}

export interface SearchFilters {
  noteType?: string;
  interviewId?: string;
  userId?: string;
}

export const useMultiInterviewSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedNotes, setHighlightedNotes] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  const parseSearchQuery = useCallback((query: string) => {
    const filters: SearchFilters = {};
    let textQuery = query;

    // Parse special filters
    const patterns = [
      { regex: /user:(\w+)/g, key: 'userId' },
      { regex: /interview:(\w+)/g, key: 'interviewId' },
      { regex: /type:(\w+)/g, key: 'noteType' },
    ];

    patterns.forEach(({ regex, key }) => {
      const matches = query.match(regex);
      if (matches) {
        filters[key as keyof SearchFilters] = matches[0].split(':')[1];
        textQuery = textQuery.replace(regex, '').trim();
      }
    });

    return { textQuery, filters };
  }, []);

  const performSearch = useCallback(async (query: string) => {
    if (!user || !query.trim()) {
      setSearchResults([]);
      setHighlightedNotes(new Set());
      return;
    }

    setIsSearching(true);
    const { textQuery, filters } = parseSearchQuery(query);

    try {
      // First, get all note spaces for the user
      let noteSpacesQuery = supabase
        .from('note_spaces')
        .select('id, name')
        .eq('user_id', user.id);

      // Apply interview filter if specified
      if (filters.interviewId) {
        noteSpacesQuery = noteSpacesQuery.eq('id', filters.interviewId);
      }

      const { data: noteSpaces, error: noteSpacesError } = await noteSpacesQuery;
      
      if (noteSpacesError) throw noteSpacesError;
      if (!noteSpaces?.length) {
        setSearchResults([]);
        return;
      }

      // Search across all flow nodes in these note spaces
      const noteSpaceIds = noteSpaces.map(ns => ns.id);
      
      let flowNodesQuery = supabase
        .from('flow_nodes')
        .select('id, note_space_id, data, position_x, position_y, node_type')
        .in('note_space_id', noteSpaceIds);

      const { data: flowNodes, error: flowNodesError } = await flowNodesQuery;
      
      if (flowNodesError) throw flowNodesError;

      // Filter and format results
      const results: SearchResult[] = [];
      const highlighted = new Set<string>();

      flowNodes?.forEach(node => {
        const noteSpace = noteSpaces.find(ns => ns.id === node.note_space_id);
        if (!noteSpace) return;

        const nodeData = node.data as any;
        const content = nodeData.content || nodeData.text || '';
        const type = nodeData.type || node.node_type || '';

        // Apply text search
        const matchesText = !textQuery || 
          content.toLowerCase().includes(textQuery.toLowerCase()) ||
          type.toLowerCase().includes(textQuery.toLowerCase());

        // Apply note type filter
        const matchesType = !filters.noteType || 
          type.toLowerCase().includes(filters.noteType.toLowerCase());

        if (matchesText && matchesType) {
          results.push({
            id: node.id,
            noteSpaceId: node.note_space_id,
            noteSpaceName: noteSpace.name,
            content,
            type,
            position: { x: node.position_x, y: node.position_y },
            data: nodeData
          });
          highlighted.add(node.id);
        }
      });

      setSearchResults(results);
      setHighlightedNotes(highlighted);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setHighlightedNotes(new Set());
    } finally {
      setIsSearching(false);
    }
  }, [user, parseSearchQuery]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setHighlightedNotes(new Set());
  }, []);

  const isNoteHighlighted = useCallback((noteId: string) => {
    return highlightedNotes.has(noteId);
  }, [highlightedNotes]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    highlightedNotes,
    isNoteHighlighted,
    clearSearch,
    hasResults: searchResults.length > 0
  };
};